import { useCallback, useEffect, useRef, useState } from "react";

/** How long a "copied" state shows before it clears. */
export const COPIED_RESET_MS = 2000;

/**
 * Copies text to the clipboard and remembers, for a moment, which one was copied (a `key` tells
 * several copy buttons on one page apart). A browser without clipboard access, or a refused write,
 * copies nothing and shows no copied state.
 */
export const useCopyToClipboard = (resetMs: number = COPIED_RESET_MS) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string, key: string = "default") => {
      if (!navigator.clipboard?.writeText) return;
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        return;
      }
      setCopiedKey(key);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopiedKey(null), resetMs);
    },
    [resetMs],
  );

  return { copiedKey, copy };
};
